import { HttpContext, HttpContextToken, HttpErrorResponse, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { AuthService } from './../services/auth.service';
import { ToastService } from './../components/toast/toast.service';

/**
 * Tratamento comum de erro HTTP.
 *
 * Nasceu como `LoadingInterceptor`, e o nome era metade da verdade: além do
 * overlay global ele sempre foi
 *
 * 1. o único lugar que mostra toast de erro de validação (422);
 * 2. o que derruba a sessão quando o corpo do 401/403 diz que o token morreu —
 *    é o que evita sessão morta em tela aberta;
 * 3. o que cala o aviso de requisição de fundo (`logout`, `no-loading`).
 *
 * O overlay saiu na CU-86akkubzz: hoje cada tela avisa sozinha, com esqueleto no
 * lugar do conteúdo que está chegando ou com carregamento no próprio botão que
 * disparou a ação. Sobrou o que o nome antigo escondia, e é o que este arquivo
 * faz agora — daí o nome novo.
 *
 * O item 2 é o mais delicado: o gatilho é o campo `status` DO CORPO mencionar
 * token, não o código HTTP. É por isso que o 403 `wallet_disabled` da carteira
 * (cujo envelope é `{ error, message }`, sem `status`) não desloga ninguém —
 * abrir uma tela sem permissão não pode custar a sessão.
 *
 * ## Por que o erro sempre sobe
 *
 * Enquanto o overlay existia, o erro de rede era ENGOLIDO nas telas que ele
 * cobria: o toast já tinha avisado e ninguém mais trataria. Isso não vale mais.
 * Toda tela agora tem uma flag própria que o desfecho da requisição desliga, e
 * engolir o erro deixaria essa flag em `true` para sempre — esqueleto brilhando
 * sem fim, botão travado girando. Por isso o `catchError` termina sempre em
 * `throw`.
 */
/**
 * A tela dona da requisição dá os próprios avisos de falha de rede (status 0) e de recusa (422).
 *
 * É para a requisição que move dinheiro: lá a falta de resposta é "desfecho incerto"
 * (confira o extrato antes de repetir), e o toast genérico "Tente novamente mais tarde"
 * diria o contrário ao mesmo tempo; e a recusa 422 tem texto próprio por `code`, que com o
 * toast daqui apareceria em duas frases diferentes. A tela usa `validationMessage` quando
 * não tem texto próprio. `HttpContext`, e não header, pelo mesmo motivo do
 * `refresh.interceptor`: header customizado em requisição cross-origin dispara preflight.
 */
export const OWN_ERROR_TOAST = new HttpContextToken<boolean>(() => false);

/** As opções de requisição com `OWN_ERROR_TOAST` (um contexto novo por chamada: `HttpContext` é mutável). */
export function ownErrorToastOptions(): { context: HttpContext } {
  return { context: new HttpContext().set(OWN_ERROR_TOAST, true) };
}

/** O aviso do interceptor para requisição sem resposta (status 0). */
export const NO_RESPONSE_MESSAGE = 'Erro ao executar operação! Tente novamente mais tarde';

/**
 * O aviso único da tela que marca `OWN_ERROR_TOAST`: sem resposta (status 0) é
 * `noResponseMessage`; o 422 sai como o interceptor o diria (`validationMessage`, que achata o
 * `errors` do Laravel); os outros status, o `message` do servidor. `fallback` quando não há texto.
 *
 * `null` no 401: esse o interceptor avisa sempre (e derruba a sessão quando o token morreu), com ou
 * sem `OWN_ERROR_TOAST` — a tela não repete.
 */
export function ownErrorMessage(err: any, fallback: string, noResponseMessage = NO_RESPONSE_MESSAGE): string | null {
  if (err?.status === 401) {
    return null;
  }

  if (err?.status === 0) {
    return noResponseMessage;
  }

  if (err?.status === 422 && err.error) {
    return validationMessage(err.error) || fallback;
  }

  return typeof err?.error?.message === 'string' && err.error.message ? err.error.message : fallback;
}

@Injectable({ providedIn: 'root' })
export class HttpErrorInterceptor implements HttpInterceptor {

  constructor(
    private toastService: ToastService,
    private authService: AuthService,
    private router: Router
  ) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<any> {

    // Requisição de fundo: logout e polling marcado com `no-loading`. É o que
    // decide se um erro de rede vira toast — um poll que falha não deve
    // interromper quem está lendo a tela.
    const isBackgroundRequest = req.url.includes('logout') || req.url.includes('no-loading');
    const ownsErrorToast = req.context.get(OWN_ERROR_TOAST);

    return next
      .handle(req)
      .pipe(catchError((err: HttpErrorResponse) => {

        if (err.status === 0 && !isBackgroundRequest && !ownsErrorToast) {
          this.toastService.error(NO_RESPONSE_MESSAGE);
        }

        // As chamadas paralelas da tela recebem o 401 juntas quando a sessão
        // morre (token expirado, membro removido da conta). Só a primeira, que
        // ainda achou a sessão, avisa; as outras chegam com ela já limpa.
        // Vale qualquer que seja o corpo: quando o refresh falha, o
        // RefreshInterceptor repassa o erro do `/refresh-token` (sem `status`
        // falando de token) a todas as que esperavam, e o AuthService já
        // limpou a sessão.
        const sessionAlreadyGone = !this.authService.isLogged();

        if ((err.status === 403 || err.status === 401) && mentionsToken(err.error)) {
          this.authService.removeData();
          this.router.navigate(['/login']);
        }

        if (err.status === 401 && !String(err.url || '').includes('login') && !sessionAlreadyGone) {
          this.toastService.error(messageOf(err.error) || 'Acesso negado!');
        }

        // Multiusuários (CU-17tm466h1cm): o membro tentou algo que é do dono
        // (cadastro, token da API, postback). 403 e não 401 para não derrubar a
        // sessão; o aviso diz o motivo em vez de a tela falhar calada.
        if (err.status === 403 && err.error?.error === 'owner_only' && !ownsErrorToast) {
          this.toastService.error(messageOf(err.error) || 'Apenas o proprietário da conta pode fazer isso.');
        }

        // `no-toast`: a tela mostra o 422 no próprio lugar (prévia e custo do RCS, que
        // rodam a cada tecla e empilhariam um toast por requisição).
        if (err.status === 422 && err.error && !ownsErrorToast && !req.url.includes('no-toast')) {
          const message = validationMessage(err.error);

          if (message) {
            this.toastService.error(message);
          }
        }

        throw err;
      }));
  }
}

/**
 * Só o campo `status` do corpo, e só quando é texto.
 *
 * O `err.error.status?.toLowerCase()` de antes lançava TypeError em 401 sem
 * corpo (`err.error` nulo) e em corpo cujo `status` é número — e um erro dentro
 * do `catchError` virava um erro diferente do que o servidor mandou.
 */
function mentionsToken(body: any): boolean {
  const status = body && typeof body === 'object' ? body.status : null;
  return typeof status === 'string' && status.toLowerCase().includes('token');
}

function messageOf(body: any): string | null {
  return body && typeof body === 'object' && typeof body.message === 'string' ? body.message : null;
}

/** Dá o aviso de `ownErrorMessage`, quando há um (no 401 o interceptor já avisou). */
export function toastOwnError(toastService: ToastService, message: string | null): void {
  if (message) {
    toastService.error(message);
  }
}

/**
 * Texto do toast de 422.
 *
 * O laço antigo iterava só o primeiro nível de `err.error` e concatenava o que
 * achasse, então o envelope padrão do Laravel — `{ message, errors: { campo:
 * [...] } }` — saía como `"The given data was invalid., [object Object]"`: o
 * objeto `errors` virava string pelo `join`. Aqui:
 *
 * - quando existe `errors`, o toast é só o que tem dentro dele, porque o
 *   `message` genérico ("The given data was invalid.") não diz nada a quem está
 *   olhando a tela;
 * - o achatamento é recursivo, então lista de mensagens por campo e objeto
 *   aninhado (campo de array, `itens.0.telefone`) chegam como frase;
 * - repetição sai, que é comum quando a mesma regra falha em vários campos;
 * - o `code` do corpo (`insufficient_funds`, `idempotency_key_reused`…) é contrato para a tela
 *   decidir o que fazer, não texto: não entra no toast.
 */
export function validationMessage(body: any): string {
  const source = hasNestedErrors(body) ? body.errors : withoutCode(body);
  const messages = flattenMessages(source);

  return messages.filter((message, index) => messages.indexOf(message) === index).join(', ');
}

function withoutCode(body: any): any {
  if (!body || typeof body !== 'object' || Array.isArray(body) || !('code' in body)) {
    return body;
  }

  const rest = { ...body };
  delete rest.code;
  return rest;
}

function hasNestedErrors(body: any): boolean {
  return !!body
    && typeof body === 'object'
    && !!body.errors
    && typeof body.errors === 'object'
    && flattenMessages(body.errors).length > 0;
}

function flattenMessages(value: any): string[] {
  if (value === null || value === undefined) {
    return [];
  }

  if (typeof value === 'string') {
    const text = value.trim();
    return text ? [text] : [];
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return [String(value)];
  }

  if (Array.isArray(value)) {
    return value.reduce<string[]>((all, item) => all.concat(flattenMessages(item)), []);
  }

  if (typeof value === 'object') {
    return Object.keys(value).reduce<string[]>((all, key) => all.concat(flattenMessages(value[key])), []);
  }

  return [];
}
